import { createRouter, createWebHistory } from "vue-router";
import store from "@/store/store";
import HomeView from "../views/User/HomeView.vue";
import LoginBox from "../views/UserRegistration/LoginBox.vue";
import SignupBox from "../views/UserRegistration/SignupBox.vue";
import ForgotPassword from "../views/UserRegistration/ForgotPassword.vue";
import ResetPassword from "../views/UserRegistration/ResetPassword.vue";
import EmailVerificationView from "../views/UserRegistration/EmailVerificationView.vue";
import AboutView from "../views/User/AboutView.vue";
import ShopView from "../views/User/ShopView.vue";
import SetView from "../views/User/SetView.vue";
import ItemView from "../views/User/ItemView.vue";
import AccountView from "../views/User/AccountView.vue";
import BlogView from "../views/User/BlogView.vue";
import ContactView from "../views/User/ContactView.vue";
import CartView from "../views/User/CartView.vue";
import CheckoutView from "../views/User/CheckoutView.vue";
import OrderSuccessView from "../views/User/OrderSuccessView.vue";
import FAQView from "../views/User/FAQView.vue";
import PrivacyPolicyView from "../views/User/PrivacyPolicyView.vue";
import TermsConditionsView from "../views/User/TermsConditionsView.vue";
import CartItemView from "../views/User/CartItemView.vue";
import PersonalizationView from "../views/User/PersonalizationView.vue";
import AdminPage from "../views/Admin/Admin Views/AdminPage.vue";
import OverviewPage from "../views/Admin/Admin Views/OverviewPage.vue";
import ProductsPage from "../views/Admin/Admin Views/ProductsPage.vue";
import OrdersPage from "../views/Admin/Admin Views/OrdersPage.vue";
import FinanceMetrics from "../views/Admin/Admin Views/FinanceMetrics.vue";
import UsersPage from "../views/Admin/Admin Views/UsersPage.vue";
import AdminManagement from "../views/Admin/Admin Views/AdminManagement.vue";
import TestPage from "../views/User/TestPage.vue";
import ShippingRatesView from "../views/User/ShippingRatesView.vue";
import BoxesView from "../views/User/BoxesView.vue";
import { auth } from "@/firebase/init";
import { onAuthStateChanged } from "firebase/auth";
import { User } from "@/firebase/userServices";

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
  },
  {
    path: '/login',
    name: 'login',
    component: LoginBox,
  },
  {
    path: '/signup',
    name: 'signup',
    component: SignupBox,
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPassword,
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: EmailVerificationView,
  },
  {
    // Handle Firebase auth action URLs (password reset, email verification, etc.)
    path: '/auth/action',
    name: 'auth-action',
    beforeEnter: (to, from, next) => {
      const mode = to.query.mode;
      const oobCode = to.query.oobCode;
      
      if (mode === 'resetPassword' && oobCode) {
        // Redirect to custom reset password page
        next({ path: '/reset-password', query: { oobCode } });
      } else if (mode === 'verifyEmail' && oobCode) {
        // Redirect to login after email verification with success message
        next({ path: '/login', query: { verified: 'true' } });
      } else {
        // For other modes, redirect to home
        next('/');
      }
    },
    component: ResetPassword, // Fallback component
  },
  {
    path: "/about",
    name: "about",
    component: AboutView,
  },
  {
    path: "/shop",
    name: "shop",
    component: ShopView,
  },
  {
    path: "/set/:id?",
    name: "set",
    component: SetView,
  },
  {
    path: "/item/:id?",
    name: "item",
    component: ItemView,
  },
  {
    path: "/account",
    name: "account",
    component: AccountView,
    meta: { requiresAuth: true },
    beforeEnter: (to, from, next) => {
      new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      }).then((user) => {
        if (user) {
          next();
        } else {
          next({ path: "/login", query: { redirect: to.fullPath } });
        }
      });
    }
  },
  {
    path: "/blog",
    name: "blog",
    component: BlogView,
  },
  {
    path: "/contact",
    name: "contact",
    component: ContactView,
  },
  {
    path: "/faq",
    name: "faq",
    component: FAQView,
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: PrivacyPolicyView,
  },
  {
    path: "/terms-conditions",
    name: "terms-conditions",
    component: TermsConditionsView,
  },
  {
    path: "/cart",
    name: "cart",
    component: CartView,
  },
  {
    path: "/checkout",
    name: "checkout",
    component: CheckoutView,
    beforeEnter: (to, from, next) => {
      // Create a promise to handle the asynchronous auth state change
      new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      }).then((user) => {
        if (user) {
          next(); 
        } else {
          next("/login");
        }
      });
    }
  },
  {
    path: "/order-success",
    name: "order-success",
    component: OrderSuccessView,
    beforeEnter: (to, from, next) => {
      new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      }).then((user) => {
        if (user) {
          next(); 
        } else {
          next("/login");
        }
      });
    }
  },
  {
    path: '/cart/:id',
    name: 'cartItem',
    component: CartItemView,
  },
  {
    path: '/personalization/:id',
    name: 'personalization',
    component: PersonalizationView,
  },
  {
    path: '/admin',
    name: 'admin',
    component: AdminPage,
    children: [
      {
        path: '/admin/overview',
        name: 'overview',
        component: OverviewPage,
      }, {
        path: '/admin/products',
        name: 'products',
        component: ProductsPage,
      }, {
        path: '/admin/orders',
        name: 'orders',
        component: OrdersPage,
      },
      {
        path: '/admin/finance',
        name: 'finance',
        component: FinanceMetrics,
      },
      {
        path: '/admin/users',
        name: 'users',
        component: UsersPage,
      },
      {
        path: '/admin/admins',
        name: 'admins',
        component: AdminManagement,
      }
    ]
  },
  {
    path: '/test',
    name: 'test',
    component: TestPage,
  }, {
    path: "/shipping-rates",
    name: "shipping-rates",
    component: ShippingRatesView,
  }, {
    path: "/boxes",
    name: "boxes",
    component: BoxesView,
  },

];


const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
});
// Admin guard for any /admin* route
router.beforeEach(async (to, from, next) => {
  if (!to.path.startsWith('/admin')) return next();
  
  // wait for auth state to resolve once
  const user = await new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
  
  if (!user) {
    console.warn('Not authenticated, redirecting to home login');
    return next('/login');
  }
  
  // ensure User state loaded then check admin (checks both hardcoded and Firestore)
  await User.getUser(user.uid);
  const isAdmin = await User.isAdminAsync();
  
  if (isAdmin) return next();
  
  console.warn('Access denied: User is not an admin');
  return next('/');
});

export default router;